import i18n from "i18next";
import { Apple, Check, Copy, Smartphone } from "lucide-react";
import { useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type * as S from "@shipyard/shared";
import { ApiError } from "@/lib/api";
import { Badge, Button, Dialog, Textarea } from "./ui";

const STATUS_TONE: Record<S.BuildStatus, "neutral" | "blue" | "green" | "amber" | "red" | "violet"> = {
  uploaded: "neutral",
  pushing: "blue",
  processing: "blue",
  testing: "violet",
  promoting: "blue",
  in_review: "amber",
  pending_release: "amber",
  released: "green",
  rejected: "red",
  failed: "red",
  superseded: "neutral",
};
const PULSE = new Set<S.BuildStatus>(["pushing", "processing", "promoting", "in_review"]);

export function StatusBadge({ status }: { status: S.BuildStatus }) {
  const { t } = useTranslation();
  return (
    <Badge tone={STATUS_TONE[status]}>
      {PULSE.has(status) && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" />}
      {t(`status.${status}`)}
    </Badge>
  );
}

export function PlatformIcon({ platform, className = "h-4 w-4" }: { platform: S.Platform; className?: string }) {
  return platform === "ios" ? <Apple className={className} aria-label="iOS" /> : <Smartphone className={className} aria-label="Android" />;
}

export function CopyButton({ value, label }: { value: string; label?: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={async () => {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? t("common.copied") : (label ?? t("common.copy"))}
    </Button>
  );
}

export function ConfirmDialog({
  trigger,
  title,
  description,
  confirmLabel,
  onConfirm,
  danger,
  children,
}: {
  trigger: (open: () => void) => ReactNode;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: ReactNode;
  onConfirm: () => Promise<unknown> | unknown;
  danger?: boolean;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <>
      {trigger(() => setOpen(true))}
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={title}
        description={description}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant={danger ? "danger" : "primary"}
              loading={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await onConfirm();
                  setOpen(false);
                } catch (err) {
                  toastError(err);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {confirmLabel ?? t("common.confirm")}
            </Button>
          </>
        }
      >
        {children}
      </Dialog>
    </>
  );
}

export function toastError(err: unknown) {
  const message = err instanceof Error ? err.message : String(err);
  // Shipyard Cloud quota reached: offer the plans.
  if (err instanceof ApiError && err.code === "plan_limit") {
    toast.error(message, { action: { label: i18n.t("billing.seePlans"), onClick: () => window.location.assign("/organization/billing") } });
    return;
  }
  toast.error(message);
}

/** Textarea that can also be filled from a local file (p8 / JSON keys). */
export function SecretInput({
  value,
  onChange,
  accept,
  placeholder,
  rows = 5,
}: {
  value: string;
  onChange: (v: string) => void;
  accept: string;
  placeholder?: string;
  rows?: number;
}) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className="space-y-2">
      <Textarea value={value} onChange={(e) => onChange(e.target.value)} rows={rows} placeholder={placeholder} className="font-mono text-xs" spellCheck={false} />
      <input
        ref={input}
        type="file"
        accept={accept}
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (file) onChange((await file.text()).trim());
          e.target.value = "";
        }}
      />
      <Button variant="outline" size="sm" onClick={() => input.current?.click()}>
        {t("admin.chooseFile")}
      </Button>
    </div>
  );
}

export function Mono({ children }: { children: ReactNode }) {
  return <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{children}</code>;
}
